import React, { useEffect, useState, useCallback } from "react";
import {
  getKitchenOrders,
  updateOrderStatus,
} from "../api";
import { setKitchenCount } from "../utils/kitchenBadge";
import notificationSound from "../assets/new-order.mp3";

export default function ChefOrdersView() {
  const [orders, setOrders] = useState([]);
  const [previousCount, setPreviousCount] = useState(0);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const loadOrders = useCallback(async () => {
    try {
      const data = await getKitchenOrders();
      const list = Array.isArray(data) ? data : [];

      // getKitchenOrders() only ever returns PENDING orders, so a
      // status-based priority sort has nothing to compare -- just sort
      // oldest-first so chefs work through the queue in order.
      list.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );

      setOrders(list);

      if (previousCount !== 0 && list.length > previousCount) {
        const audio = new Audio(notificationSound);
        audio.play().catch(() => {});
      }

      setPreviousCount(list.length);
      setKitchenCount(list.length);

      if (list.length === 0) {
        setMsg("No kitchen orders.");
      } else {
        setMsg("");
      }
    } catch (err) {
      console.error(err);
      setMsg("Failed to load kitchen orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();

    const interval = setInterval(loadOrders, 5000);

    return () => clearInterval(interval);
  }, [loadOrders]);

  const handleStatus = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, status);
      await loadOrders();
    } catch (err) {
      console.error(err);
      alert("Unable to update order.");
    }
  };

  // getKitchenOrders() only ever returns PENDING orders -- the chef should
  // never see a READY card, so there's nothing to branch on here anymore.
  const getStatusColor = () => {
    return "so-chef-card-pending";
  };

  // Sums quantities across all line items (not just the count of distinct
  // menu items) -- shared by the Large Order badge and the Total Items line
  // so it's only calculated once per order per render.
  const getTotalItems = (order) =>
    (order.orderItems || []).reduce((sum, item) => sum + item.quantity, 0);

  // "X min ago" style elapsed time so chefs can spot orders that have been
  // waiting a while. Recalculated on every render, which happens at least
  // every 5s thanks to the polling interval above.
  const getElapsedLabel = (createdAt) => {
  if (!createdAt) return "";

  const minutes = Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(createdAt).getTime()) / 60000
    )
  );

  // Less than 1 minute
  if (minutes < 1) {
    return "just now";
  }

  // Less than 1 hour
  if (minutes < 60) {
    if (minutes === 1) {
      return "1 min ago";
    }

    return `${minutes} min ago`;
  }

  // 1 hour or more
  const hours = Math.floor(minutes / 60);

  if (hours === 1) {
    return "1 hour ago";
  }

  return `${hours} hours ago`;
};


  return (
    <div className="so-chef p-6">

      {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-chef {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --amber: #c9892f;
          --amber-dark: #9c6a1f;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
          background-size: 24px 24px;
          font-family: var(--font-body);
          color: var(--ink);
          min-height: 100%;
        }

        .so-chef * {
          box-sizing: border-box;
        }

        .so-chef-title {
          font-family: var(--font-display);
          font-size: 2rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-chef-subtitle {
          color: var(--stone);
          font-size: 0.9rem;
        }

        .so-chef-counter {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 10px;
          box-shadow: 0 10px 22px -18px rgba(28, 26, 23, 0.3);
        }

        .so-chef-counter-label {
          font-family: var(--font-mono);
          font-size: 0.7rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--stone);
        }

        .so-chef-counter-value {
          font-family: var(--font-display);
          font-size: 1.8rem;
          color: var(--chili);
        }

        .so-chef-loading,
        .so-chef-empty {
          color: var(--stone-light);
        }

        .so-chef-card {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
        }

        .so-chef-card-pending {
          border-left: 8px solid var(--amber);
        }

        .so-chef-card-header {
          border-bottom: 1px solid var(--line);
        }

        .so-chef-order-number {
          font-family: var(--font-display);
          font-size: 1.3rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-chef-timestamp {
          font-size: 0.72rem;
          color: var(--stone-light);
          margin-top: 4px;
        }

        .so-chef-elapsed {
          font-weight: 700;
          color: var(--chili-dark);
        }

        .so-chef-badge-dinein {
          background: #e9eef5;
          color: #35537d;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 600;
        }

        .so-chef-badge-takeaway {
          background: #eaf3ee;
          color: var(--herb-dark);
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 600;
        }

        .so-chef-badge-large {
          background: #fdece6;
          color: var(--chili-dark);
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 700;
        }

        .so-chef-status-pending {
          background: #fbeedc;
          color: var(--amber-dark);
          border-radius: 999px;
          font-size: 0.82rem;
          font-weight: 600;
        }

        .so-chef-table-line {
          color: var(--ink);
        }

        .so-chef-table-label {
          font-weight: 700;
          color: var(--stone);
        }

        .so-chef-notes {
          border: 1px solid #e3c488;
          background: #fbf1da;
          border-radius: 10px;
        }

        .so-chef-notes-title {
          font-weight: 700;
          color: var(--amber-dark);
        }

        .so-chef-notes-body {
          color: var(--ink);
          margin-top: 4px;
        }

        .so-chef-total-items {
          font-size: 0.85rem;
          color: var(--stone);
        }

        .so-chef-item-row {
          border: 1px solid var(--line);
          border-radius: 10px;
          background: var(--paper-dim);
        }

        .so-chef-item-name {
          font-weight: 600;
          color: var(--ink);
        }

        .so-chef-item-qty {
          font-family: var(--font-display);
          font-size: 1.3rem;
          color: var(--chili);
        }

        .so-chef-ready-btn {
          width: 100%;
          border-radius: 8px;
          color: var(--paper);
          font-weight: 700;
          background: var(--herb);
          box-shadow: 0 10px 22px -10px rgba(63, 107, 79, 0.5);
          transition: background 0.15s ease;
        }

        .so-chef-ready-btn:hover {
          background: var(--herb-dark);
        }
      `}</style>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="so-chef-title">Kitchen Display</h2>
          <p className="so-chef-subtitle">Pending Orders</p>
        </div>

        <div className="so-chef-counter px-4 py-2">
          <span className="so-chef-counter-label">Active Orders</span>
          <div className="so-chef-counter-value">{orders.length}</div>
        </div>
      </div>

      {loading && (
        <div className="so-chef-loading text-center py-10">
          Loading Kitchen Orders...
        </div>
      )}

      {!loading && msg && (
        <div className="so-chef-empty text-center py-10">{msg}</div>
      )}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {orders.map((order) => (
          <div
            key={order.id}
            className={`so-chef-card ${getStatusColor()}`}
          >
            {/* Header */}
            <div className="so-chef-card-header p-4 flex justify-between">
              <div>
                <h3 className="so-chef-order-number">
                  #{order.orderNumber}
                  <div className="so-chef-timestamp">
                    {new Date(order.createdAt).toLocaleString()}
                    {" · "}
                    <span className="so-chef-elapsed">
                      {getElapsedLabel(order.createdAt)}
                    </span>
                  </div>
                </h3>

                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {order.orderType === "DINE_IN" ? (
                    <span className="so-chef-badge-dinein inline-block px-2 py-1">
                      🍽 DINE IN
                    </span>
                  ) : (
                    <span className="so-chef-badge-takeaway inline-block px-2 py-1">
                      🥡 TAKEAWAY
                    </span>
                  )}

                  {getTotalItems(order) >= 5 && (
                    <span className="so-chef-badge-large px-2 py-1">
                      Large Order
                    </span>
                  )}
                </div>
              </div>

              <div>
                {/* getKitchenOrders() only returns PENDING orders, so this
                    is always Pending -- no READY branch needed here. */}
                <span className="so-chef-status-pending px-3 py-1">
                  Pending
                </span>
              </div>
            </div>

            <div className="p-4">
              {/* Table (dine-in only) */}
              {order.orderType === "DINE_IN" && order.table && (
                <div className="so-chef-table-line mb-4">
                  <span className="so-chef-table-label">Table :</span>{" "}
                  {order.table.tableName}
                </div>
              )}

              {/* Kitchen Notes -- shown once per order, not per item */}
              {order.specialInstructions &&
                order.specialInstructions.trim() !== "" && (
                  <div className="so-chef-notes mb-4 p-3">
                    <div className="so-chef-notes-title">
                      📝 Kitchen Notes
                    </div>
                    <div className="so-chef-notes-body">
                      {order.specialInstructions}
                    </div>
                  </div>
                )}

              {/* Total item count, so the chef knows the size of the ticket
                  at a glance without counting each line */}
              <div className="so-chef-total-items mb-4">
                Total Items : {getTotalItems(order)}
              </div>

              {/* Items */}
              <div className="space-y-3">
                {(order.orderItems || []).map((item) => (
                  <div
                    key={item.id}
                    className="so-chef-item-row p-3"
                  >
                    <div className="flex justify-between">
                      <div className="so-chef-item-name">{item.menuItem?.name}</div>
                      <div className="so-chef-item-qty">x{item.quantity}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Status Button -- order.status is always PENDING here, so
                  there's no disabled/READY state to account for anymore. */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    if (window.confirm("Mark this order as READY?")) {
                      handleStatus(order.id, "READY");
                    }
                  }}
                  className="so-chef-ready-btn py-3"
                >
                  Ready
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}




// import React, { useEffect, useState, useCallback } from "react";
// import {
//   getKitchenOrders,
//   updateOrderStatus,
// } from "../api";
// import { setKitchenCount } from "../utils/kitchenBadge";
// import notificationSound from "../assets/new-order.mp3";

// export default function ChefOrdersView() {
//   const [orders, setOrders] = useState([]);
//   const [previousCount, setPreviousCount] = useState(0);
//   const [msg, setMsg] = useState("");
//   const [loading, setLoading] = useState(true);

//   const loadOrders = useCallback(async () => {
//     try {
//       const data = await getKitchenOrders();
//       const list = Array.isArray(data) ? data : [];

//       // getKitchenOrders() only ever returns PENDING orders, so a
//       // status-based priority sort has nothing to compare -- just sort
//       // oldest-first so chefs work through the queue in order.
//       list.sort(
//         (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
//       );

//       setOrders(list);

//       if (previousCount !== 0 && list.length > previousCount) {
//         const audio = new Audio(notificationSound);
//         audio.play().catch(() => {});
//       }

//       setPreviousCount(list.length);
//       setKitchenCount(list.length);

//       if (list.length === 0) {
//         setMsg("No kitchen orders.");
//       } else {
//         setMsg("");
//       }
//     } catch (err) {
//       console.error(err);
//       setMsg("Failed to load kitchen orders.");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     loadOrders();

//     const interval = setInterval(loadOrders, 5000);

//     return () => clearInterval(interval);
//   }, [loadOrders]);

//   const handleStatus = async (orderId, status) => {
//     try {
//       await updateOrderStatus(orderId, status);
//       await loadOrders();
//     } catch (err) {
//       console.error(err);
//       alert("Unable to update order.");
//     }
//   };

//   // getKitchenOrders() only ever returns PENDING orders -- the chef should
//   // never see a READY card, so there's nothing to branch on here anymore.
//   const getStatusColor = () => {
//     return "border-l-8 border-yellow-500";
//   };

//   // Sums quantities across all line items (not just the count of distinct
//   // menu items) -- shared by the Large Order badge and the Total Items line
//   // so it's only calculated once per order per render.
//   const getTotalItems = (order) =>
//     (order.orderItems || []).reduce((sum, item) => sum + item.quantity, 0);

//   // "X min ago" style elapsed time so chefs can spot orders that have been
//   // waiting a while. Recalculated on every render, which happens at least
//   // every 5s thanks to the polling interval above.
//   const getElapsedLabel = (createdAt) => {
//   if (!createdAt) return "";

//   const minutes = Math.max(
//     0,
//     Math.floor(
//       (Date.now() - new Date(createdAt).getTime()) / 60000
//     )
//   );

//   // Less than 1 minute
//   if (minutes < 1) {
//     return "just now";
//   }

//   // Less than 1 hour
//   if (minutes < 60) {
//     if (minutes === 1) {
//       return "1 min ago";
//     }

//     return `${minutes} min ago`;
//   }

//   // 1 hour or more
//   const hours = Math.floor(minutes / 60);

//   if (hours === 1) {
//     return "1 hour ago";
//   }

//   return `${hours} hours ago`;
// };


//   return (
//     <div className="p-6">
//       <div className="flex justify-between items-center mb-6">
//         <div>
//           <h2 className="text-3xl font-bold">Kitchen Display</h2>
//           <p className="text-slate-500">Pending Orders</p>
//         </div>

//         <div className="bg-white shadow rounded-lg px-4 py-2">
//           <span className="text-sm text-slate-500">Active Orders</span>
//           <div className="text-2xl font-bold">{orders.length}</div>
//         </div>
//       </div>

//       {loading && (
//         <div className="text-center py-10 text-slate-500">
//           Loading Kitchen Orders...
//         </div>
//       )}

//       {!loading && msg && (
//         <div className="text-center py-10 text-slate-500">{msg}</div>
//       )}

//       <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
//         {orders.map((order) => (
//           <div
//             key={order.id}
//             className={`bg-white rounded-xl shadow border border-slate-200 overflow-hidden ${getStatusColor()}`}
//           >
//             {/* Header */}
//             <div className="border-b p-4 flex justify-between">
//               <div>
//                 <h3 className="font-bold text-lg">
//                   #{order.orderNumber}
//                   <div className="text-xs text-slate-500 mt-1">
//                     {new Date(order.createdAt).toLocaleString()}
//                     {" · "}
//                     <span className="font-semibold text-slate-600">
//                       {getElapsedLabel(order.createdAt)}
//                     </span>
//                   </div>
//                 </h3>

//                 <div className="flex flex-wrap items-center gap-2 mt-2">
//                   {order.orderType === "DINE_IN" ? (
//                     <span className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
//                       🍽 DINE IN
//                     </span>
//                   ) : (
//                     <span className="inline-block bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
//                       🥡 TAKEAWAY
//                     </span>
//                   )}

//                   {getTotalItems(order) >= 5 && (
//                     <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-semibold">
//                       Large Order
//                     </span>
//                   )}
//                 </div>
//               </div>

//               <div>
//                 {/* getKitchenOrders() only returns PENDING orders, so this
//                     is always Pending -- no READY branch needed here. */}
//                 <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm">
//                   Pending
//                 </span>
//               </div>
//             </div>

//             <div className="p-4">
//               {/* Table (dine-in only) */}
//               {order.orderType === "DINE_IN" && order.table && (
//                 <div className="mb-4">
//                   <span className="font-semibold text-slate-700">Table :</span>{" "}
//                   {order.table.tableName}
//                 </div>
//               )}

//               {/* Kitchen Notes -- shown once per order, not per item */}
//               {order.specialInstructions &&
//                 order.specialInstructions.trim() !== "" && (
//                   <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-3">
//                     <div className="font-semibold text-yellow-800">
//                       📝 Kitchen Notes
//                     </div>
//                     <div className="mt-1 text-slate-700">
//                       {order.specialInstructions}
//                     </div>
//                   </div>
//                 )}

//               {/* Total item count, so the chef knows the size of the ticket
//                   at a glance without counting each line */}
//               <div className="mb-4 text-sm text-slate-500">
//                 Total Items : {getTotalItems(order)}
//               </div>

//               {/* Items */}
//               <div className="space-y-3">
//                 {(order.orderItems || []).map((item) => (
//                   <div
//                     key={item.id}
//                     className="border rounded-lg p-3 bg-slate-50"
//                   >
//                     <div className="flex justify-between">
//                       <div className="font-medium">{item.menuItem?.name}</div>
//                       <div className="font-bold text-lg">x{item.quantity}</div>
//                     </div>
//                   </div>
//                 ))}
//               </div>

//               {/* Status Button -- order.status is always PENDING here, so
//                   there's no disabled/READY state to account for anymore. */}
//               <div className="mt-6">
//                 <button
//                   onClick={() => {
//                     if (window.confirm("Mark this order as READY?")) {
//                       handleStatus(order.id, "READY");
//                     }
//                   }}
//                   className="w-full py-3 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-700"
//                 >
//                   Ready
//                 </button>
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }
